import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

// Account Linking Modal Component
const AccountLinkingModal = ({ existingAccount, onLink, onCancel, isLoading }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="glass border border-light-border dark:border-border rounded-2xl p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display text-xl italic text-light-text dark:text-pure-white">
            Account Found
          </h3>
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-pure-white disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          {/* Existing Account Info */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <p className="text-sm text-light-text dark:text-silver mb-2">
              An account with this email already exists:
            </p>
            <div className="bg-white/5 rounded-lg p-3">
              <p className="font-semibold text-light-text dark:text-pure-white">
                {existingAccount.firstName} {existingAccount.lastName}
              </p>
              <p className="text-sm text-light-text-secondary dark:text-silver/60">
                {existingAccount.email}
              </p>
              <p className="text-xs text-light-text-secondary dark:text-silver/60 mt-1">
                Account type: <span className="capitalize">{existingAccount.role?.replace('_', ' ')}</span>
              </p>
            </div>
          </div>

          {/* Explanation */}
          <div className="text-sm text-light-text-secondary dark:text-silver/60">
            <p className="font-medium text-light-text dark:text-silver mb-2">
              What would you like to do?
            </p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <svg className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span><strong>Link accounts:</strong> Sign in to your existing account using Google. You'll be able to use either Google or your password to log in.</span>
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span><strong>Cancel:</strong> Keep accounts separate. You can sign in with your password instead.</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1 py-2 px-4 rounded-lg border border-light-border dark:border-border text-light-text dark:text-silver hover:bg-light-bg/50 dark:hover:bg-white/5 transition-smooth disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onLink}
              disabled={isLoading}
              className="flex-1 btn-primary disabled:opacity-50"
            >
              {isLoading ? 'Linking...' : 'Link Accounts'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Google logo SVG
const GoogleLogo = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

// User-friendly error messages for OAuth failures
const getOAuthErrorMessage = (error, isNetworkError = false) => {
  if (isNetworkError) {
    return 'Unable to connect to Google. Please check your internet connection and try again.';
  }

  // Handle specific error types
  const errorMessage = error?.message?.toLowerCase() || '';
  const errorString = String(error).toLowerCase();

  if (errorMessage.includes('cancelled') || errorMessage.includes('canceled') ||
      errorString.includes('cancelled') || errorString.includes('canceled') ||
      errorMessage.includes('user denied') || errorMessage.includes('access_denied')) {
    return 'Sign-in was cancelled. You can try again when you\'re ready.';
  }

  if (errorMessage.includes('popup') || errorMessage.includes('blocked')) {
    return 'Pop-up was blocked. Please allow pop-ups for this site and try again.';
  }

  if (errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
    return 'The request timed out. Please check your internet connection and try again.';
  }

  if (errorMessage.includes('invalid') || errorMessage.includes('token')) {
    return 'Authentication failed. Please try signing in again.';
  }

  // Default user-friendly message
  return error?.message || 'Something went wrong with Google sign-in. Please try again.';
};

// Check if an error is a network error
const isNetworkError = (error) => {
  if (!error) return false;
  const message = error?.message?.toLowerCase() || '';
  return (
    error.name === 'TypeError' ||
    error.name === 'NetworkError' ||
    message.includes('network') ||
    message.includes('fetch') ||
    message.includes('failed to fetch') ||
    message.includes('connection') ||
    message.includes('cors') ||
    message.includes('internet')
  );
};

const GoogleSignInButton = ({ role, onSuccess, onError, disabled, className = '', mode = 'signup' }) => {
  const { googleLogin } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showDevModal, setShowDevModal] = useState(false);
  const [showLinkingModal, setShowLinkingModal] = useState(false);
  const [pendingLinkAccount, setPendingLinkAccount] = useState(null);
  const [devFormData, setDevFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });

  // Button text based on mode
  const buttonText = mode === 'login' ? 'Sign in with Google' : 'Sign up with Google';
  const loadingText = mode === 'login' ? 'Signing in...' : 'Connecting...';

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      // First, check if Google OAuth is configured
      const response = await fetch(`${API_URL}/auth/google/url?role=${role}`);

      if (!response.ok) {
        throw new Error('Failed to connect to authentication server');
      }

      const data = await response.json();

      if (data.devMode) {
        // Development mode - show dev modal for simulated OAuth
        setShowDevModal(true);
        setIsLoading(false);
      } else {
        // Production mode - redirect to Google OAuth
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      const networkError = isNetworkError(error);
      const userMessage = getOAuthErrorMessage(error, networkError);
      onError?.(userMessage);
      setIsLoading(false);
    }
  };

  const handleDevSubmit = async (confirmLinkAccount = false) => {
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/google/dev`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...devFormData,
          role,
          confirmLinkAccount,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Google sign-in failed');
      }

      // Check if account linking is required
      if (data.requiresLinkConfirmation) {
        // Store the pending link info and show linking modal
        setPendingLinkAccount({
          existingAccount: data.existingAccount,
          formData: { ...devFormData },
        });
        setShowDevModal(false);
        setShowLinkingModal(true);
        setIsLoading(false);
        return;
      }

      // Update auth context with the Google login data
      await googleLogin(data);

      // Close modals and call success handler
      setShowDevModal(false);
      setShowLinkingModal(false);
      setPendingLinkAccount(null);
      onSuccess?.(data);
    } catch (error) {
      console.error('Dev Google sign-in error:', error);
      const networkError = isNetworkError(error);
      const userMessage = getOAuthErrorMessage(error, networkError);
      onError?.(userMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLinkAccount = async () => {
    if (!pendingLinkAccount) return;

    // Restore the form data and resubmit with confirmation
    setDevFormData(pendingLinkAccount.formData);

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/auth/google/dev`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...pendingLinkAccount.formData,
          role,
          confirmLinkAccount: true,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to link accounts');
      }

      // Update auth context with the Google login data
      await googleLogin(data);

      // Close modals and call success handler
      setShowLinkingModal(false);
      setPendingLinkAccount(null);
      onSuccess?.(data);
    } catch (error) {
      console.error('Account linking error:', error);
      const networkError = isNetworkError(error);
      const userMessage = getOAuthErrorMessage(error, networkError);
      onError?.(userMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelLink = () => {
    setShowLinkingModal(false);
    setPendingLinkAccount(null);
    // Optionally notify that linking was cancelled (graceful handling)
    // Not calling onError here since this is intentional user action
  };

  // Handle dev modal cancellation (user closes modal without completing)
  const handleDevModalClose = () => {
    setShowDevModal(false);
    setDevFormData({ firstName: '', lastName: '', email: '' });
    // Graceful handling - user intentionally cancelled
  };

  return (
    <>
      <button
        type="button"
        onClick={handleGoogleSignIn}
        disabled={disabled || isLoading}
        className={`flex items-center justify-center gap-3 w-full py-3 px-6 rounded-xl border border-light-border dark:border-border
          bg-white dark:bg-obsidian/50 hover:bg-light-bg/80 dark:hover:bg-obsidian text-light-text dark:text-pure-white
          font-mono text-sm uppercase tracking-widest transition-smooth
          disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      >
        <GoogleLogo />
        {isLoading ? loadingText : buttonText}
      </button>

      {/* Development Mode Modal */}
      {showDevModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="glass border border-light-border dark:border-border rounded-2xl p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-display text-xl italic text-light-text dark:text-pure-white">
                Dev Mode: Google Sign-In
              </h3>
              <button
                type="button"
                onClick={handleDevModalClose}
                className="text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-pure-white"
                aria-label="Close Google sign-in"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="text-sm text-light-text-secondary dark:text-silver/60 mb-4">
              {mode === 'login'
                ? 'Google OAuth not configured. Enter your email to sign in (account must exist):'
                : 'Google OAuth not configured. Enter simulated user info for testing:'}
            </p>

            <div className="space-y-4">
              <div>
                <label className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={devFormData.firstName}
                  onChange={(e) => setDevFormData({ ...devFormData, firstName: e.target.value })}
                  className="input-field"
                  placeholder="John"
                />
              </div>
              <div>
                <label className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={devFormData.lastName}
                  onChange={(e) => setDevFormData({ ...devFormData, lastName: e.target.value })}
                  className="input-field"
                  placeholder="Doe"
                />
              </div>
              <div>
                <label className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={devFormData.email}
                  onChange={(e) => setDevFormData({ ...devFormData, email: e.target.value })}
                  className="input-field"
                  placeholder="john.doe@gmail.com"
                />
              </div>

              <div className="text-xs text-light-text-secondary dark:text-silver/60 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                {mode === 'login' ? (
                  <>
                    <span className="text-amber-400">This simulates Google OAuth login for development.</span>
                    <br />
                    <span>If the email doesn't exist, a new account will be created.</span>
                  </>
                ) : (
                  <>
                    <strong>Role:</strong> {role?.replace('_', ' ') || 'patient'}
                    <br />
                    <span className="text-amber-400">This simulates Google OAuth for development.</span>
                  </>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleDevModalClose}
                  className="flex-1 py-2 px-4 rounded-lg border border-light-border dark:border-border text-light-text dark:text-silver hover:bg-light-bg/50 dark:hover:bg-white/5 transition-smooth"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleDevSubmit(false)}
                  disabled={isLoading || !devFormData.firstName || !devFormData.lastName || !devFormData.email}
                  className="flex-1 btn-primary disabled:opacity-50"
                >
                  {isLoading
                    ? (mode === 'login' ? 'Signing in...' : 'Creating...')
                    : (mode === 'login' ? 'Sign In' : 'Create Account')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Account Linking Modal */}
      {showLinkingModal && pendingLinkAccount && (
        <AccountLinkingModal
          existingAccount={pendingLinkAccount.existingAccount}
          onLink={handleLinkAccount}
          onCancel={handleCancelLink}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default GoogleSignInButton;
